import { useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "./query-keys";

interface RegistrationItem {
  id: number;
  created_at: string;
  name: string;
  surname: string;
  phone: string;
  email: string;
  age: number | null;
  interests: string[];
  interest_other?: string | null;
  banks: string[];
  property_bought?: string | null;
  value_amount?: string | null;
  developer?: string | null;
  address?: string | null;
  coupons_printed_at?: string | null;
  coupon_ready_at?: string | null;
  received_gift_at?: string | null;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface RegistrationDetailResponse {
  item: RegistrationItem;
}

interface RegistrationListResponse {
  items: RegistrationItem[];
  pagination: PaginationInfo;
}

interface RegistrationListParams {
  page?: number;
  limit?: number;
  search?: string;
}

export function useRegistrationDetail(id: string | number | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: queryKeys.registrations.detail(id ?? ""),
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/admin/registrations/${id}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch registration: ${response.statusText}`);
      }

      return response.json() as Promise<RegistrationDetailResponse>;
    },
    enabled: !!id,
  });
}

export function useRegistrationList(params: RegistrationListParams = {}) {
  const authenticatedFetch = useAuthenticatedFetch();
  const { page = 1, limit = 50, search = "" } = params;

  return useQuery({
    queryKey: queryKeys.registrations.list({ page, limit, search }),
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (search) {
        searchParams.append("search", search);
      }

      const response = await authenticatedFetch(
        `/api/admin/registrations?${searchParams.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch registrations: ${response.statusText}`);
      }

      return response.json() as Promise<RegistrationListResponse>;
    },
  });
}

interface StatsResponse {
  totalRegistrations: number;
  todayRegistrations: number;
  giftsDistributed: number;
  couponsReady: number;
  totalSales: number;
  todaySales: number;
}

export function useRegistrationStats() {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: queryKeys.registrations.stats,
    queryFn: async () => {
      const response = await authenticatedFetch("/api/admin/stats");

      if (!response.ok) {
        throw new Error(`Failed to fetch registration stats: ${response.statusText}`);
      }

      return response.json() as Promise<StatsResponse>;
    },
  });
}

interface TimelineDataPoint {
  time_period: string;
  registrations: number;
  coupons_ready: number;
  coupons_printed: number;
  gifts_distributed: number;
}

interface TimelineResponse {
  timeline: TimelineDataPoint[];
  granularity: string;
}

export function useRegistrationTimeline(
  range: "today" | "7d" = "7d",
  granularity: "daily" | "hourly" = "daily"
) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: [...queryKeys.registrations.stats, "timeline", range, granularity],
    queryFn: async () => {
      // Get timezone offset in minutes (e.g., -420 for PDT, 420 for Bangkok)
      const timezoneOffset = new Date().getTimezoneOffset();
      // Note: getTimezoneOffset returns positive for west of UTC, negative for east
      // We need to negate it for the API (e.g., Bangkok UTC+7 should be 420)
      const tzOffset = -timezoneOffset;

      const response = await authenticatedFetch(
        `/api/admin/stats/timeline?range=${range}&granularity=${granularity}&tzOffset=${tzOffset}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch timeline data: ${response.statusText}`);
      }

      return response.json() as Promise<TimelineResponse>;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

export function useUpdateRegistrationGift() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, received_gift }: { id: number; received_gift: boolean }) => {
      const response = await authenticatedFetch(`/api/admin/registrations/${id}/gift`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ received_gift }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update gift status: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: (_data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ["registrations", "list"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.stats });
    },
  });
}

export function useMarkCouponsPrinted() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const response = await authenticatedFetch(`/api/admin/registrations/${id}/print-coupons`, {
        method: "PATCH",
      });

      if (!response.ok) {
        throw new Error(`Failed to mark coupons as printed: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: (_data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ["registrations", "list"] });
    },
  });
}

interface UpdateRegistrationParams {
  id: number;
  name?: string;
  surname?: string;
  phone?: string;
  email?: string;
  age?: number | null;
  interests?: string[];
  interestOther?: string | null;
  banks?: string[];
  property_bought?: string | null;
  value_amount?: string | null;
  developer?: string | null;
  address?: string | null;
}

export function useUpdateRegistration() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateRegistrationParams) => {
      const response = await authenticatedFetch(`/api/registrations/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to update registration: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: (_data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ["registrations", "list"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.stats });
    },
  });
}
